import React from 'react'
import PropTypes from 'prop-types'
import FormX from 'sb-formx'
import actions from 'redux/actions'
import { useCallback, useMemo } from 'sb-hooks'
import { mixpanel, constants } from 'common/helpers'
import { minLength, required } from 'helpers/validation'

import Box from 'components/_new/layout/Box/Box'
import Button from 'components/_new/Button/Button'
import Text from 'components/_new/typography/Text/Text'
import Field from 'components/_new/forms/Field/Field'
import FieldState from 'components/_new/forms/FieldState/FieldState'
import { RadioGroup, RadioButton } from 'components/_new/forms/radio'

import s from './FeedbackForm.scss'
import messages from './messages'


const choiceValues = [ 'don\'t cancel', 'cancel' ]

const form = new FormX({
  name: 'FeedbackForm',
  fields: {
    message: [ required, minLength(10) ],
    choice: {
      validate: [ required ],
      value: choiceValues[0],
    },
  },
})

const handleInputFocus = () => {
  mixpanel.track(constants.analyticsEvents.cancelFlow.feedback.activate)
}

const FeedbackForm = (props) => {
  const {
    // TODO "buttonProps", "successModalText" never passed to this component
    buttonProps, successModalText, unsubscribeReason, messageRows,
    showSuccessModal, withFreeMonthText,
    onSuccess,
  } = props

  const choiceOptions = useMemo(() => ([
    {
      value: choiceValues[0],
      text: withFreeMonthText ? messages.optionWaitFreeMonth : messages.optionWait,
    },
    {
      value: choiceValues[1],
      text: messages.optionCancel,
    },
  ]), [ withFreeMonthText ])

  const handleSubmit = useCallback((event) => {
    event.preventDefault()

    form.submit()
      .then((values) => {
        const isUnsubscribeSelected = values.choice === 'cancel'

        const data = {
          message: values.message,
          reason: unsubscribeReason,
          isUnsubscribeSelected,
        }

        mixpanel.track(constants.analyticsEvents.cancelFlow.feedback.send, { type: data.choice })

        const handleSuccess = (data) => {
          form.unsetValues()

          if (showSuccessModal) {
            actions.models.modals.open(constants.modals.humble.FeedbackFormSuccess, { text: successModalText })
          }

          if (typeof onSuccess === 'function') {
            onSuccess({ data, isUnsubscribeSelected })
          }
        }

        actions.view.cancelSubscriptionPage.sendFeedback(data)
          .then(handleSuccess, () => {
            actions.models.modals.open(constants.modals.cancelSubscription.FeedbackFormFail, {
              values: data,
              onSuccess: handleSuccess,
            })
          })
      })
  }, [ unsubscribeReason, successModalText, onSuccess ])

  return (
    <form noValidate onSubmit={handleSubmit}>
      <Field
        label={messages.label}
        type="text"
        field={form.fields.message}
        placeholder={messages.label}
        multiline
        rows={messageRows}
        onFocus={handleInputFocus}
      />
      <Box className={s.row} mt="24">
        <RadioGroup field={form.fields.choice}>
          {
            choiceOptions.map(({ text, value }) => (
              <RadioButton key={value} className={s.option} value={value}>
                <Text className={s.optionText} message={text} tag="span" />
              </RadioButton>
            ))
          }
        </RadioGroup>
        <div className={s.buttonContainer}>
          <FieldState field={form.fields.message}>
            {
              ({ value }) => (
                <Button
                  className={s.button}
                  title={messages.buttonTitle}
                  type="submit"
                  color="brand"
                  size="sm"
                  disabled={!value}
                  {...buttonProps}
                />
              )
            }
          </FieldState>
        </div>
      </Box>
    </form>
  )
}

FeedbackForm.propTypes = {
  onSuccess: PropTypes.func,
  buttonProps: PropTypes.object,
  showSuccessModal: PropTypes.bool,
  successModalText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  unsubscribeReason: PropTypes.string,
  messageRows: PropTypes.number,
  withFreeMonthText: PropTypes.bool,
}

FeedbackForm.defaultProps = {
  messageRows: 8,
  showSuccessModal: true,
}


export default FeedbackForm
